import { createInlineFeedbackController } from '../../../components/inline-feedback.js';
import { readAccountPageCsrfToken } from '../dom.js';
import { submitAccountImportExecution, submitAccountImportValidation } from './api.js';
import { ACCOUNT_IMPORT_RESULT_KIND, ACCOUNT_IMPORT_STATE } from './constants.js';
import { resolveAccountImportDom } from './dom.js';

export function initAccountImport(documentRef, fetchImpl, page) {
    const dom = resolveAccountImportDom(page);
    if (dom === null) {
        return;
    }

    const {
        card,
        confirmCheckbox,
        confirmation,
        defaultTriggerLabel,
        defaultExecuteTriggerLabel,
        executeTrigger,
        executeUrl,
        feedback,
        form,
        input,
        selection,
        trigger,
        validateUrl,
    } = dom;
    const feedbackController = createInlineFeedbackController({ documentRef, feedback });
    let destroyed = false;
    let currentState = ACCOUNT_IMPORT_STATE.IDLE;

    const resetConfirmation = () => {
        confirmation.hidden = true;
        confirmCheckbox.checked = false;
        confirmCheckbox.disabled = true;
        executeTrigger.disabled = true;
        executeTrigger.textContent = defaultExecuteTriggerLabel;
    };

    const setState = (state) => {
        currentState = state;
        card.dataset.accountImportState = state;
        input.disabled = state === ACCOUNT_IMPORT_STATE.VALIDATING || state === ACCOUNT_IMPORT_STATE.IMPORTING;

        switch (state) {
            case ACCOUNT_IMPORT_STATE.READY_TO_VALIDATE:
                trigger.disabled = false;
                trigger.textContent = defaultTriggerLabel;
                resetConfirmation();
                break;
            case ACCOUNT_IMPORT_STATE.VALIDATING:
                trigger.disabled = true;
                trigger.textContent = 'Validating...';
                resetConfirmation();
                break;
            case ACCOUNT_IMPORT_STATE.VALID:
                trigger.disabled = false;
                trigger.textContent = defaultTriggerLabel;
                confirmation.hidden = false;
                confirmCheckbox.disabled = false;
                executeTrigger.disabled = !confirmCheckbox.checked;
                executeTrigger.textContent = defaultExecuteTriggerLabel;
                break;
            case ACCOUNT_IMPORT_STATE.READY_TO_IMPORT:
                trigger.disabled = false;
                trigger.textContent = defaultTriggerLabel;
                confirmation.hidden = false;
                confirmCheckbox.disabled = false;
                executeTrigger.disabled = false;
                executeTrigger.textContent = defaultExecuteTriggerLabel;
                break;
            case ACCOUNT_IMPORT_STATE.IMPORTING:
                trigger.disabled = true;
                trigger.textContent = defaultTriggerLabel;
                confirmation.hidden = false;
                confirmCheckbox.disabled = true;
                executeTrigger.disabled = true;
                executeTrigger.textContent = 'Importing...';
                break;
            case ACCOUNT_IMPORT_STATE.IMPORTED:
                trigger.disabled = false;
                trigger.textContent = defaultTriggerLabel;
                resetConfirmation();
                break;
            default:
                trigger.disabled = !hasSelectedFile(input);
                trigger.textContent = defaultTriggerLabel;
                resetConfirmation();
                break;
        }
    };

    const clearValidationResult = () => {
        feedbackController.clear();
    };

    const syncSelection = () => {
        if (currentState === ACCOUNT_IMPORT_STATE.VALIDATING) {
            return;
        }

        const file = readSelectedFile(input);
        if (file === null) {
            selection.hidden = true;
            selection.textContent = '';
            clearValidationResult();
            setState(ACCOUNT_IMPORT_STATE.IDLE);

            return;
        }

        selection.textContent = `Selected: ${normalizeFileName(file.name)}`;
        selection.hidden = false;
        clearValidationResult();
        setState(ACCOUNT_IMPORT_STATE.READY_TO_VALIDATE);
    };

    const renderValidResult = (result) => {
        feedbackController.showContent('success', ({ createElement }) => {
            feedback.dataset.revealPhase = feedback.dataset.revealPhase === 'a' ? 'b' : 'a';

            const title = createElement('p');
            title.className = 'account-import-feedback-title';
            title.textContent = `Validation passed for schema ${result.schemaVersion}`;

            const intro = createElement('p');
            intro.textContent = 'Review the archive summary below, then confirm the replacement import.';

            const list = createElement('dl');
            list.className = 'account-import-summary';

            appendSummaryItem(createElement, list, 'Games', result.summary.games);
            appendSummaryItem(createElement, list, 'Manual lists', result.summary.manual_lists);
            appendSummaryItem(createElement, list, 'Rankings', result.summary.rankings);
            appendSummaryItem(createElement, list, 'Notes', result.summary.notes);
            appendSummaryItem(createElement, list, 'Attachments', result.summary.note_attachments);
            appendSummaryItem(createElement, list, 'Statuses', result.summary.dictionary_statuses);
            appendSummaryItem(createElement, list, 'Hardware', result.summary.dictionary_hardware);
            appendSummaryItem(createElement, list, 'Platforms', result.summary.dictionary_platforms);
            appendSummaryItem(createElement, list, 'Tags', result.summary.dictionary_tags);

            feedback.append(title, intro, list);
        });
    };

    const renderInvalidResult = (result) => {
        feedbackController.showContent('warning', ({ createElement }) => {
            const message = createElement('p');
            message.textContent = result.message;
            // The message explains the reason; its machine code is not for the owner.
            feedback.append(message);
        });
    };

    const syncConfirmationState = () => {
        if (destroyed) {
            return;
        }

        if (currentState !== ACCOUNT_IMPORT_STATE.VALID
            && currentState !== ACCOUNT_IMPORT_STATE.READY_TO_IMPORT) {
            confirmCheckbox.checked = false;
            executeTrigger.disabled = true;
            return;
        }

        setState(confirmCheckbox.checked ? ACCOUNT_IMPORT_STATE.READY_TO_IMPORT : ACCOUNT_IMPORT_STATE.VALID);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (destroyed || currentState === ACCOUNT_IMPORT_STATE.VALIDATING) {
            return;
        }

        const file = readSelectedFile(input);
        if (file === null) {
            clearValidationResult();
            setState(ACCOUNT_IMPORT_STATE.IDLE);
            return;
        }

        setState(ACCOUNT_IMPORT_STATE.VALIDATING);
        feedbackController.showMessage('Checking archive structure and attachment limits...', 'warning');

        try {
            const result = await submitAccountImportValidation(fetchImpl, {
                csrfToken: readAccountPageCsrfToken(documentRef),
                file,
                form,
                validateUrl,
            });

            if (destroyed) {
                return;
            }

            if (result.kind === ACCOUNT_IMPORT_RESULT_KIND.VALID) {
                setState(ACCOUNT_IMPORT_STATE.VALID);
                renderValidResult(result);
                return;
            }

            if (result.kind === ACCOUNT_IMPORT_RESULT_KIND.INVALID) {
                setState(ACCOUNT_IMPORT_STATE.INVALID);
                renderInvalidResult(result);
                return;
            }

            setState(ACCOUNT_IMPORT_STATE.FAILED);
            feedbackController.showMessage(result.message, result.tone);
        } catch (error) {
            if (destroyed) {
                return;
            }

            console.error(error);
            setState(ACCOUNT_IMPORT_STATE.FAILED);
            feedbackController.showMessage(
                'Import validation is temporarily unavailable. Please try again later.',
                'error',
            );
        }
    };

    const handleExecute = async (event) => {
        event.preventDefault();

        if (destroyed
            || currentState !== ACCOUNT_IMPORT_STATE.READY_TO_IMPORT
            || !confirmCheckbox.checked) {
            return;
        }

        const file = readSelectedFile(input);
        if (file === null) {
            clearValidationResult();
            setState(ACCOUNT_IMPORT_STATE.IDLE);
            return;
        }

        setState(ACCOUNT_IMPORT_STATE.IMPORTING);
        feedbackController.showMessage('Replacing current library with validated archive...', 'warning');

        try {
            const result = await submitAccountImportExecution(fetchImpl, {
                csrfToken: readAccountPageCsrfToken(documentRef),
                executeUrl,
                file,
                form,
            });

            if (destroyed) {
                return;
            }

            if (result.kind === ACCOUNT_IMPORT_RESULT_KIND.IMPORTED) {
                setState(ACCOUNT_IMPORT_STATE.IMPORTED);
                feedbackController.showMessage('Import complete. Your library has been replaced.', 'success');
                return;
            }

            if (result.kind === ACCOUNT_IMPORT_RESULT_KIND.INVALID) {
                setState(ACCOUNT_IMPORT_STATE.INVALID);
                renderInvalidResult(result);
                return;
            }

            setState(ACCOUNT_IMPORT_STATE.FAILED);
            feedbackController.showMessage(result.message, result.tone);
        } catch (error) {
            if (destroyed) {
                return;
            }

            console.error(error);
            setState(ACCOUNT_IMPORT_STATE.FAILED);
            feedbackController.showMessage(
                'Import is temporarily unavailable. Please try again later.',
                'error',
            );
        }
    };

    input.addEventListener('change', syncSelection);
    confirmCheckbox.addEventListener('change', syncConfirmationState);
    executeTrigger.addEventListener('click', handleExecute);
    form.addEventListener('submit', handleSubmit);
    setState(ACCOUNT_IMPORT_STATE.IDLE);

    return {
        destroy() {
            destroyed = true;
            input.removeEventListener('change', syncSelection);
            confirmCheckbox.removeEventListener('change', syncConfirmationState);
            executeTrigger.removeEventListener('click', handleExecute);
            form.removeEventListener('submit', handleSubmit);
            if (Array.isArray(input.files)) {
                input.files = [];
            }
            input.value = '';
            selection.hidden = true;
            selection.textContent = '';
            clearValidationResult();
            currentState = ACCOUNT_IMPORT_STATE.IDLE;
            card.dataset.accountImportState = ACCOUNT_IMPORT_STATE.IDLE;
            input.disabled = false;
            trigger.disabled = true;
            trigger.textContent = defaultTriggerLabel;
            resetConfirmation();
        },
    };
}

function readSelectedFile(input) {
    if (!Array.isArray(input.files) && (typeof input.files !== 'object' || input.files === null)) {
        return null;
    }

    return input.files[0] ?? null;
}

function normalizeFileName(name) {
    const normalized = typeof name === 'string' ? name.trim() : '';

    return normalized !== '' ? normalized : 'archive.zip';
}

function hasSelectedFile(input) {
    return readSelectedFile(input) !== null;
}

function appendSummaryItem(createElement, list, label, value) {
    const term = createElement('dt');
    term.textContent = label;

    const description = createElement('dd');
    description.textContent = String(value);
    description.className = value === 0
        ? 'account-import-summary-value account-import-summary-value--zero'
        : 'account-import-summary-value account-import-summary-value--active';

    list.append(term, description);
}
